import React, { useEffect, useState } from "react";
import { TrendingUp, AlertTriangle } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Spinner } from "@/components/kit";
import { fmtDate } from "@/lib/vda";

// Mileage-over-time for a single vehicle, read from the customer-scoped safe view
// v_vehicle_mileage (RLS: can_access_customer OR is_internal_staff). Renders a
// compact SVG sparkline + the most recent readings, and flags implausible entries.
function Sparkline({ points }) {
  if (points.length < 2) return null;
  const w = 240, h = 48, pad = 4;
  const xs = points.map((p) => p.t);
  const ys = points.map((p) => p.m);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  const nx = (x) => pad + ((x - minX) / (maxX - minX || 1)) * (w - 2 * pad);
  const ny = (y) => h - pad - ((y - minY) / (maxY - minY || 1)) * (h - 2 * pad);
  const d = points.map((p, i) => `${i ? "L" : "M"}${nx(p.t).toFixed(1)},${ny(p.m).toFixed(1)}`).join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="text-primary" data-testid="mileage-sparkline">
      <path d={d} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round" strokeLinecap="round" />
      {points.map((p, i) => <circle key={`${p.t}-${p.m}`} cx={nx(p.t)} cy={ny(p.m)} r="1.8" fill="currentColor" />)}
    </svg>
  );
}

export function MileageTrend({ vehicleId }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let m = true;
    supabase.from("v_vehicle_mileage").select("*").eq("vehicle_id", vehicleId)
      .order("observed_at", { ascending: true })
      .then(({ data }) => { if (m) setRows(data || []); });
    return () => { m = false; };
  }, [vehicleId]);

  if (rows === null) return <div className="py-3"><Spinner label="Loading mileage" /></div>;
  if (rows.length === 0) return <p className="text-sm text-muted-foreground py-2" data-testid="mileage-empty">No mileage readings recorded yet.</p>;

  const points = rows.map((r) => ({ t: new Date(r.observed_at).getTime(), m: r.mileage }));
  const latest = rows[rows.length - 1];
  const first = rows[0];
  const delta = latest.mileage - first.mileage;

  return (
    <div className="space-y-3" data-testid="mileage-trend">
      <div className="flex items-center justify-between">
        <div>
          <span className="vda-tick text-[hsl(var(--vda-quiet))]">Latest</span>
          <div className="font-mono text-xl font-semibold tabular-nums">{latest.mileage.toLocaleString()} mi</div>
        </div>
        {rows.length > 1 && (
          <div className="text-right">
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary"><TrendingUp className="h-3.5 w-3.5" /> +{delta.toLocaleString()} mi</span>
            <div className="text-[11px] text-muted-foreground">over {rows.length} readings</div>
          </div>
        )}
      </div>
      <Sparkline points={points} />
      <div className="border-t border-border">
        {[...rows].reverse().slice(0, 6).map((r) => (
          <div key={r.id} className="flex items-center justify-between py-1.5 text-sm border-b border-border last:border-0">
            <span className="text-muted-foreground">{fmtDate(r.observed_at)}</span>
            <span className="flex items-center gap-2">
              {r.flagged && <span className="inline-flex items-center gap-1 text-[11px] text-[hsl(var(--vda-amber))]" data-testid="mileage-flagged"><AlertTriangle className="h-3 w-3" />{r.flagged === "below_previous" ? "below previous" : "large jump"}</span>}
              <span className="font-mono tabular-nums">{r.mileage.toLocaleString()}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
